import { loadBricksImperatively, BrickLoadError } from "@next-core/loader";
import { HttpResponseError } from "@next-core/http";
import { i18n } from "@next-core/i18n";
import { httpErrorToString } from "../handleHttpError.js";
import { RenderTag } from "./enums.js";
import { _internalApiGetPresetBricks, getBrickPackages } from "./Runtime.js";
import { K, NS } from "./i18n.js";
import { getBasePath } from "../getBasePath.js";
export class PageError extends Error {
  constructor(message) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(message);
    this.name = "PageError";

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    // istanbul ignore else
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BrickLoadError);
    }
  }
}

/**
 * Will always resolve
 */
export async function ErrorNode(error, returnNode, pageLevel) {
  const {
    title,
    variant,
    showLink,
    showDescription
  } = getRefinedErrorConf(error);
  if (pageLevel) {
    var _presetBricks$error;
    const presetBricks = _internalApiGetPresetBricks();
    const errorBrick = (_presetBricks$error = presetBricks.error) !== null && _presetBricks$error !== void 0 ? _presetBricks$error : "illustrations.error-message";
    if (errorBrick !== false) {
      const linkBrick = "eo-link";
      const bricks = (showLink ? [errorBrick, linkBrick] : [errorBrick]).filter(brick => !customElements.get(brick));
      try {
        await Promise.race([loadBricksImperatively(bricks, getBrickPackages()),
        // Timeout after 3 seconds
        new Promise((_resolve, reject) => setTimeout(() => {
          reject(new Error("timeout"));
        }, 3e3))]);
        const node = {
          tag: RenderTag.BRICK,
          type: errorBrick,
          properties: {
            errorTitle: title,
            description: showDescription ? httpErrorToString(error) : undefined,
            variant,
            dataset: {
              errorBoundary: ""
            }
          },
          runtimeContext: null,
          return: returnNode
        };
        if (showLink) {
          node.child = getLinkNode(showLink, linkBrick, node, "");
        }
        return node;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load brick:", bricks.join(", "), e);
      }
    }
  }
  const useDefaultError = pageLevel && !!customElements.get("easyops-default-error");
  const node = {
    tag: RenderTag.BRICK,
    type: useDefaultError ? "easyops-default-error" : "div",
    properties: {
      errorTitle: title,
      dataset: {
        errorBoundary: ""
      },
      style: {
        color: "var(--color-error)"
      }
    },
    runtimeContext: null,
    return: returnNode
  };
  const descriptionNode = {
    tag: RenderTag.BRICK,
    type: "div",
    properties: {
      textContent: useDefaultError ? showDescription ? httpErrorToString(error) : "" : showDescription ? `${title}: ${httpErrorToString(error)}` : title
    },
    runtimeContext: null,
    return: node
  };
  node.child = descriptionNode;
  if (pageLevel && showLink) {
    descriptionNode.sibling = getLinkNode(showLink, "a", node, useDefaultError ? "link" : "");
  }
  return node;
}
function getLinkNode(showLink, brick, parent, slotId) {
  return {
    tag: RenderTag.BRICK,
    type: brick,
    slotId,
    properties: {
      textContent: showLink === "home" ? i18n.t(`${NS}:${K.GO_BACK_HOME}`) : showLink === "reload" ? i18n.t(`${NS}:${K.RELOAD}`) : i18n.t(`${NS}:${K.GO_BACK_TO_PREVIOUS_PAGE}`),
      href: showLink === "home" ? getBasePath() : showLink === "reload" ? location.href : undefined
    },
    events: showLink === "previous" ? {
      click: {
        action: "history.goBack"
      }
    } : undefined,
    runtimeContext: null,
    return: parent
  };
}
function getRefinedErrorConf(error) {
  var _error$response, _error$responseJson;
  if (error instanceof PageError) {
    return error.message === "page blocked" ? {
      showLink: "home",
      title: i18n.t(`${NS}:${K.LICENSE_BLOCKED}`),
      variant: "no-permission"
    } : error.message === "app not found" ? {
      showLink: "home",
      title: i18n.t(`${NS}:${K.APP_NOT_FOUND}`),
      variant: "no-permission"
    } : {
      showLink: "home",
      variant: "not-found",
      title: i18n.t(`${NS}:${K.PAGE_NOT_FOUND}`)
    };
  }
  if (error instanceof BrickLoadError || error instanceof Error && error.name === "ChunkLoadError" || error instanceof Event && error.type === "error" && error.target instanceof HTMLScriptElement) {
    return {
      showDescription: true,
      title: i18n.t(`${NS}:${K.NETWORK_ERROR}`),
      variant: "internet-disconnected",
      showLink: "reload"
    };
  }
  if (error instanceof HttpResponseError && ((_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.status) === 403) {
    return {
      showLink: "previous",
      showDescription: true,
      title: i18n.t(`${NS}:${K.NO_PERMISSION}`),
      variant: "no-permission"
    };
  }
  if (error instanceof HttpResponseError && ((_error$responseJson = error.responseJson) === null || _error$responseJson === void 0 ? void 0 : _error$responseJson.code) === "200000") {
    return {
      showDescription: true,
      title: i18n.t(`${NS}:${K.LICENSE_EXPIRED}`),
      variant: "license-expired"
    };
  }
  return {
    showLink: "previous",
    showDescription: true,
    title: i18n.t(`${NS}:${K.UNKNOWN_ERROR}`),
    variant: "unknown-error"
  };
}
//# sourceMappingURL=ErrorNode.js.map